import { initializeApp, FirebaseOptions } from 'firebase/app';
import {
  getMessaging,
  getToken,
  onMessage,
  Messaging,
  isSupported,
} from 'firebase/messaging';
import { getInstallations, getId, Installations } from 'firebase/installations';

import { NotificationService } from '../../../haropa/components/notification/notification';

declare const STAGE: string;
declare const customscripts: { nonce: string };

/**
 * Service qui récupère le Firebase Installation ID (FID) + le token FCM,
 * les envoie à l’API WordPress et tient compte des rotations de jeton.
 */
export class FirebaseMessagingService {
  // Firebase
  private messaging!: Messaging;
  private installations!: Installations;
  private vapidKey: string;
  private config: FirebaseOptions;

  private notificationService: NotificationService | null = null;;

  constructor() {
    // ---------- CONFIGS ----------
    const stagingConfig: FirebaseOptions = {
      apiKey: 'AIzaSyCU06yL_qqnGj7xSrPTOhHedRGdwGfAGwU',
      authDomain: 'haropa-dev.firebaseapp.com',
      projectId: 'haropa-dev',
      storageBucket: 'haropa-dev.firebasestorage.app',
      messagingSenderId: '212559516528',
      appId: '1:212559516528:web:c1285eb4d71caf83bfb5b5',
    };

    this.config = stagingConfig;
    this.vapidKey =
      'BEUAOTkmWoSutI73XOCeAXQ0nkTCizgRNaEWuuuPWqRBGUuN3wzEjn5FhwuEWaesrsipFZpauuxcBnjmo9Qd1Zc';

    if (STAGE === 'production') {
      const prodConfig = {
        apiKey: 'AIzaSyB9LqZQSEFtJv-ze2dO9qsMl0TSlc1IsZU',
        authDomain: 'haropa-prod.firebaseapp.com',
        projectId: 'haropa-prod',
        storageBucket: 'haropa-prod.firebasestorage.app',
        messagingSenderId: '97940857224',
        appId: '1:97940857224:web:2dc92b0cd86280f3d8da3d',
      };
      this.config = prodConfig;
      this.vapidKey =
        'BKUBOXRQZX-WlFtI1n0mLm-u73DhQW9P0zavldvam7SwnycPXvrsSsaBGTlEzkYTrtXw0xXaBvW5DLtlb-iYy6M';
    }

    // Le SDK Messaging n’est disponible que sur HTTPS et si le navigateur le supporte.
    //this.askPermission();
    this.notificationService = new NotificationService();
  }

  public async init(): Promise<void> {
    await this._initFirebase();
  }

  private async _initFirebase() {
    const supported = await isSupported();
    // (window as any)._mobileLog?.(`isSupported: ${supported}`);
    if (!supported) return;

    try {
      const app = initializeApp(this.config);
      this.messaging = getMessaging(app);
      this.installations = getInstallations(app);

      const fid = await getId(this.installations);
      // (window as any)._mobileLog?.(`FID: ${fid}`);
      const initialToken = await this._getCurrentToken();
      // (window as any)._mobileLog?.(`token: ${initialToken ? initialToken.substring(0, 20) + '...' : 'NULL'}`);
      await this._sendTokenToServer(fid, initialToken);

      // Réception des notifications en foreground
      onMessage(this.messaging, (payload) => {
        // console.log('Notification reçue en foreground:', payload);
        this.notificationService?.displayNotification(payload);
      });

    } catch (error) {
      console.error('Firebase init error:', error);
    }
  }

  public async askPermission() {
    const permission = await Notification.requestPermission();

    if (permission !== 'granted') return false;

    // Si le token n'a pas pu être obtenu pendant l'init (permission non accordée),
    // on l'enregistre maintenant que la permission vient d'être accordée.
    if (this.installations) {
      const fid = await getId(this.installations);
      const token = await this._getCurrentToken();
      // (window as any)._mobileLog?.(`askPermission token: ${token ? token.substring(0, 20) + '...' : 'NULL'}`);
      await this._sendTokenToServer(fid, token);
    }

    return true;
  }


  private async _getCurrentToken(): Promise<string | null> {
    try {
      const token = await getToken(this.messaging, { vapidKey: this.vapidKey });
      // (window as any)._mobileLog?.(`token OK: ${token ? token.substring(0, 20) + '...' : 'NULL'}`);
      return token;
    } catch (err: any) {
      // (window as any)._mobileLog?.(`getToken ERROR: ${err?.message ?? err}`);
      console.error('Erreur lors de getToken FCM:', err);
      return null;
    }
  }


  private async _sendTokenToServer(
    fid: string,
    token: string | null,
  ) {
    if (!token) return;
    try {
      await fetch('/wp-json/wp/v2/register', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-WP-Nonce': customscripts.nonce
        },
        body: JSON.stringify({ fid, token })
      });
    } catch (e) {
      console.error('Échec d’envoi du token au serveur:', e);
    }
  }

  // * localStorage — topics par appareil
  private _localStorageKey(): string {
    return `fcm_topics`;
  }

  public getLocalTopics(): string[] {
    try {
      const raw = localStorage.getItem(this._localStorageKey());
      return raw ? JSON.parse(raw) : [];
    } catch {
      return [];
    }
  }

  private _saveLocalTopics(topics: string[]): void {
    try {
      localStorage.setItem(this._localStorageKey(), JSON.stringify(topics));
    } catch {}
  }

  //  * 3. Subscribe  (POST /wp/v2/topics)
  public async subscribeToTopics(topics: string[]) {
    if (topics.length === 0) {
      return;
    }

    try {
      const fid = await getId(this.installations);

      const res = await fetch('/wp-json/wp/v2/topics', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-WP-Nonce': customscripts.nonce
        },
        body: JSON.stringify({ fid, topics })
      });
      const data = await res.json();
      // (window as any)._mobileLog?.(`subscribe ${res.status}:`, data);
      if (res.ok) {
        const current = this.getLocalTopics();
        this._saveLocalTopics(Array.from(new Set(current.concat(topics))));
      }
    } catch (e) {
      console.error('Échec de la souscription des topics:', e);
    }
  }

  // * 4. Unsubscribe  (DELETE /wp/v2/topics)
  public async unsubscribeFromTopics(topics: string[]) {
    if (topics.length === 0) {
      return;
    }

    try {
      const fid = await getId(this.installations);

      const res = await fetch('/wp-json/wp/v2/topics', {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          'X-WP-Nonce': customscripts.nonce
        },
        body: JSON.stringify({ fid, topics })
      });
      if (res.ok) {
        const current = this.getLocalTopics();
        this._saveLocalTopics(current.filter(t => !topics.includes(t)));
      }
    } catch (e) {
      console.error('Échec de la désinscription des topics:', e);
    }
  }

  //  * 5. Liste des topics  (GET /wp/v2/topics?fid=XYZ)
  public async getTopicsSubscribed() {
    try {
      const fid = await getId(this.installations);
      // (window as any)._mobileLog?.(`getTopics FID: ${fid}`);
      const urlWithParams = `/wp-json/wp/v2/topics?fid=${fid}`;
      const response = await fetch(urlWithParams, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'X-WP-Nonce': customscripts.nonce
        },
      });
      if (!response.ok) {
        console.error('Erreur du serveur lors de la récupération des sujets.');
        return null;
      }

      const data = await response.json();
      // (window as any)._mobileLog?.(`getTopics response:`, data);
      return data;

    } catch (error) {
      console.error('Échec de la récupération des sujets souscrits:', error);
      return null;
    }
  }

}

export const firebaseMessagingService = new FirebaseMessagingService();
